import { useEffect, useRef } from 'react';

import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import { PrimaryProductKeys } from '../../../common/constants/primary-product-keys';
import { usePrimaryProduct } from '../../../controllers/store/bootstrap';

import { preloadSearchDialog } from './async';

/**
 * Decides *when* the dialog chunk is fetched, never whether it is split — `SearchDialogAsync` is
 * used unconditionally, since a static import on the control path would keep the dialog in the
 * eager bundle for everyone and the test cohort would save nothing.
 *
 * - control, and every product but Confluence: fetch on mount, preserving today's timing.
 * - test: wait for hover or focus on the input, which strongly predicts opening. The keyboard
 *   shortcut is the one path that pays the fetch at open time.
 *
 * The product check runs before the experiment read so ineligible users fire no exposure.
 */
export function useSearchDialogPreload(isInteracted: boolean): void {
	const [primaryProduct] = usePrimaryProduct();

	// Latched on the first render where the product is known, and nothing preloads before then.
	// `primaryProduct` starts undefined, so reading it eagerly would latch "not Confluence" and
	// disable the experiment, while preloading meanwhile would defeat the deferral being measured.
	const shouldDeferRef = useRef<boolean | undefined>(undefined);
	if (shouldDeferRef.current === undefined && primaryProduct !== undefined) {
		shouldDeferRef.current =
			primaryProduct === PrimaryProductKeys.Confluence &&
			isExperimentEnabled('quick_find_lazy_dialog');
	}
	const shouldDefer = shouldDeferRef.current;

	useEffect(() => {
		if (shouldDefer === undefined) {
			return;
		}

		if (!shouldDefer || isInteracted) {
			preloadSearchDialog();
		}
	}, [shouldDefer, isInteracted]);
}
